import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { DOCUMENT } from '../constants/documentTheme';
import api from '../config/api';
import { knownCityCoords } from '../utils/knownCityCoordinates';
import {
  picksToMapPois,
  suggestionCoords,
} from '../utils/aiSuggestionPlaces';
import { scheduleMapLayoutUpdate } from '../utils/renderRoadTripLeafletMap';
import {
  SUGGESTION_POI_COLORS,
  SUGGESTION_POI_LETTERS,
  highlightSuggestionPoi,
  renderSuggestionPoisOnMap,
} from '../utils/renderSuggestionPoiLeafletMap';

const MAP_LEGEND = [
  { category: 'hotels', labelKey: 'kindHotels' },
  { category: 'restaurants', labelKey: 'kindRestaurants' },
  { category: 'locations', labelKey: 'kindPlaces' },
  { category: 'activities', labelKey: 'kindActivities' }
];

const geocodeCache = new Map();

async function geocodePlace(query) {
  const key = String(query || '').trim().toLowerCase();
  if (!key) return null;
  if (geocodeCache.has(key)) return geocodeCache.get(key);
  const known = knownCityCoords(query);
  if (known) {
    geocodeCache.set(key, known);
    return known;
  }
  try {
    const response = await api.get('/geocoding', { params: { city: query } });
    const coords = response.data?.coordinates || response.data;
    const lat = Number(coords?.lat);
    const lng = Number(coords?.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      geocodeCache.set(key, null);
      return null;
    }
    const point = { lat, lng };
    geocodeCache.set(key, point);
    return point;
  } catch {
    geocodeCache.set(key, null);
    return null;
  }
}

const AiSuggestionPoiMap = ({ picks = [], destinations = [], hoveredPickKey = null }) => {
  const { t } = useTranslation();
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef({});
  const resolvedRef = useRef({});
  const [extraCoords, setExtraCoords] = useState({});
  const [mapCenter, setMapCenter] = useState(null);
  const geocodeSignature = useMemo(
    () => [
      destinations.join('|'),
      ...picks.map((pick) => `${pick.key}::${pick.city || pick.item?.city || ''}`)
    ].join('\n'),
    [picks, destinations]
  );

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      const cityCoords = {};
      const cities = [...new Set([
        ...destinations,
        ...picks.map((pick) => pick.city || pick.item?.city || '').filter(Boolean)
      ])];

      for (const city of cities) {
        if (cancelled) return;
        try {
          const center = await geocodePlace(city);
          if (!center) continue;
          cityCoords[String(city).trim().toLowerCase()] = center;
          if (!cancelled) {
            setMapCenter((current) => current || center);
          }
        } catch {
          // Keep looking up the next city.
        }
      }

      const next = {};
      picks.forEach((pick) => {
        if (suggestionCoords(pick.item) || resolvedRef.current[pick.key]) return;
        const city = String(pick.city || pick.item?.city || '').trim().toLowerCase();
        const coords = cityCoords[city];
        if (!coords) return;
        resolvedRef.current[pick.key] = coords;
        next[pick.key] = coords;
      });

      if (!cancelled && Object.keys(next).length) {
        setExtraCoords((prev) => ({ ...prev, ...next }));
      }
    };

    run();
    return () => { cancelled = true; };
    // Nearby enrichment can recreate `picks` without changing names/addresses.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geocodeSignature]);

  const pois = useMemo(
    () => picksToMapPois(picks, extraCoords),
    [picks, extraCoords]
  );

  useEffect(() => {
    if (!mapRef.current || (!pois.length && !mapCenter)) return undefined;
    if (mapInstanceRef.current) {
      mapInstanceRef.current.remove?.();
      mapInstanceRef.current = null;
    }

    const map = L.map(mapRef.current, {
      zoomControl: true,
      scrollWheelZoom: false
    });
    if (!map) return undefined;
    mapInstanceRef.current = map;
    const rendered = renderSuggestionPoisOnMap(map, pois, { fallbackCenter: mapCenter });
    markersRef.current = rendered?.markers || {};
    scheduleMapLayoutUpdate(map);
    const layoutTimer = window.setTimeout(() => scheduleMapLayoutUpdate(map), 350);

    return () => {
      window.clearTimeout(layoutTimer);
      map.remove?.();
      if (mapInstanceRef.current === map) {
        mapInstanceRef.current = null;
      }
      markersRef.current = {};
    };
  }, [pois, mapCenter]);

  useEffect(() => {
    highlightSuggestionPoi(markersRef.current, hoveredPickKey);
    const marker = hoveredPickKey ? markersRef.current[hoveredPickKey] : null;
    if (marker?.getLatLng && mapInstanceRef.current?.panTo) {
      mapInstanceRef.current.panTo(marker.getLatLng(), { animate: true, duration: 0.25 });
    }
  }, [hoveredPickKey, pois]);

  useEffect(() => {
    if (!hoveredPickKey) return undefined;
    const pick = picks.find((row) => row.key === hoveredPickKey);
    if (!pick || suggestionCoords(pick.item) || extraCoords[hoveredPickKey]) return undefined;
    let cancelled = false;
    (async () => {
      const city = pick.city || pick.item?.city || '';
      try {
        const coords = city ? await geocodePlace(city) : null;
        if (!cancelled && coords) {
          resolvedRef.current[hoveredPickKey] = coords;
          setExtraCoords((prev) => ({ ...prev, [hoveredPickKey]: coords }));
        }
      } catch {
        // Leave the pin off until a later lookup succeeds.
      }
    })();
    return () => { cancelled = true; };
  }, [hoveredPickKey, picks, extraCoords]);

  if (!picks.length) return null;

  return (
    <Box data-testid="partner-trip-ai-map" data-hovered-key={hoveredPickKey || ''} sx={{ mb: 0 }}>
      <Typography
        sx={{
          fontSize: 11,
          letterSpacing: '0.11em',
          textTransform: 'uppercase',
          color: DOCUMENT.ink3,
          mb: 1
        }}
      >
        {t('partnerAi.mapHeading')}
      </Typography>
      <Box
        ref={mapRef}
        data-pin-count={pois.length}
        sx={{
          height: 280,
          border: `1px solid ${DOCUMENT.ruleHard}`,
          bgcolor: DOCUMENT.canvas,
          '& .leaflet-container': {
            height: '100%',
            width: '100%',
            fontFamily: DOCUMENT.sans,
            background: DOCUMENT.canvas
          },
          '& .ai-suggestion-poi-hot': {
            zIndex: 800,
            animation: 'aiPoiPulse 1.1s ease-in-out infinite'
          },
          '@keyframes aiPoiPulse': {
            '0%': { filter: 'drop-shadow(0 0 0 rgba(225, 29, 72, 0.0))' },
            '50%': { filter: 'drop-shadow(0 0 10px rgba(225, 29, 72, 0.7))' },
            '100%': { filter: 'drop-shadow(0 0 0 rgba(225, 29, 72, 0.0))' }
          }
        }}
      />
      <Typography sx={{ fontSize: 12, color: DOCUMENT.ink3, mt: 0.75 }}>
        {pois.length
          ? t('partnerAi.mapHint', { count: pois.length })
          : t('partnerAi.mapLoading')}
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 1 }}>
        {MAP_LEGEND.map(({ category, labelKey }) => (
          <Box key={category} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box
              sx={{
                width: 18,
                height: 18,
                borderRadius: '50%',
                bgcolor: SUGGESTION_POI_COLORS[category],
                color: '#F6EFE4',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 10,
                fontWeight: 700,
                border: '2px solid #fff',
                boxShadow: '0 1px 3px rgba(0,0,0,0.22)'
              }}
            >
              {SUGGESTION_POI_LETTERS[category]}
            </Box>
            <Typography sx={{ fontSize: 11, color: DOCUMENT.ink3 }}>
              {t(`partnerAi.${labelKey}`)}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default AiSuggestionPoiMap;
